import { DestroyRef } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, merge, Observable } from 'rxjs';
import { map, skip, startWith, switchMap } from 'rxjs/operators';
import { AssetSelectionTypeEnum } from '../../../../../models/enum/shared/asset-selection-type.enum';
import { VariableBenefitConfigurationJurisdictionDetailFormGroup } from '../../../../records/components/members/ip-member-record/components/ip-member-record-variable-benefit-configuration/form-groups/variable-benefit-configuration-jurisdiction-detail-form-group';
import { LockedUnlockedBalance } from '../../../../../models/shared/locked-unlocked-balance';
import { CreateMonthlyPensionAccountDcJurisdictionDetailFormGroup } from '../../../../records/components/members/ip-member-record/components/ip-member-record-create-monthly-pension-account/form-groups/create-monthly-pension-account-dc-jurisdiction-detail-form-group';
import { GoverningJurisdictionTypeId } from '../../../../../models/enum/shared/governing-jurisdiction-type-id';
import { GOVERNING_JURISDICTION_NAMES } from '../../../../../consts/consts';
import { NumberUtils } from '../../../../../utils/number-utils';

export type BalanceSection = 'locked' | 'unlocked';

/**
 * Normalizes a /lockedunlockedbalances response into one entry per governing jurisdiction so the
 * asset selection step can key rows by jurisdiction:
 * - Entries sharing a jurisdiction are merged by summing their balances.
 * - The Non-Locked-In entry (null jurisdiction) holds funds that are non-locked-in by definition,
 *   so its funds always count toward the unlocked side — falling back to totalBalance when the
 *   locked/unlocked split is not populated for it.
 */
export function normalizeLockedUnlockedBalances(balances: LockedUnlockedBalance[]): LockedUnlockedBalance[] {
  const byJurisdiction = new Map<GoverningJurisdictionTypeId | null, LockedUnlockedBalance>();

  balances.forEach(balance => {
    const isNonLockedIn = balance.governingJurisdictionId === null;
    const locked = isNonLockedIn ? 0 : balance.lockedInBalance ?? 0;
    const unlocked = isNonLockedIn
      ? (balance.unlockedBalance || balance.totalBalance) ?? 0
      : balance.unlockedBalance ?? 0;

    const existing = byJurisdiction.get(balance.governingJurisdictionId);
    if (existing) {
      existing.lockedInBalance += locked;
      existing.unlockedBalance += unlocked;
      existing.totalBalance = existing.lockedInBalance + existing.unlockedBalance;
      return;
    }

    const normalized = Object.assign(new LockedUnlockedBalance(), balance);
    normalized.lockedInBalance = locked;
    normalized.unlockedBalance = unlocked;
    normalized.totalBalance = locked + unlocked;
    byJurisdiction.set(balance.governingJurisdictionId, normalized);
  });

  return Array.from(byJurisdiction.values());
}

/**
 * Sort comparator for balance entries: named jurisdictions alphabetically, with the
 * Non-Locked-In entry (null jurisdiction) last.
 */
export function compareBalancesByJurisdictionName(a: LockedUnlockedBalance, b: LockedUnlockedBalance): number {
  if (a.governingJurisdictionId === null) return b.governingJurisdictionId === null ? 0 : 1;
  if (b.governingJurisdictionId === null) return -1;
  const aName = GOVERNING_JURISDICTION_NAMES[a.governingJurisdictionId] ?? '';
  const bName = GOVERNING_JURISDICTION_NAMES[b.governingJurisdictionId] ?? '';
  return aName.localeCompare(bName);
}

/**
 * Default asset selection type for a fresh form: "All Non-Locked-In" only when there is
 * exactly one jurisdiction holding only non-locked-in funds (unlocked > 0, locked = 0);
 * otherwise "Specify Amount".
 */
export function defaultAssetSelectionType(balances: LockedUnlockedBalance[]): AssetSelectionTypeEnum {
  const isSingleNonLockedOnly =
    balances.length === 1 && balances[0].unlockedBalance > 0 && balances[0].lockedInBalance === 0;
  return isSingleNonLockedOnly ? AssetSelectionTypeEnum.AllNonLockedIn : AssetSelectionTypeEnum.SpecifyAmount;
}

/**
 * Floors a dollar value to a whole cent, rounding away sub-micro-dollar float noise first so a
 * value that is already an exact number of cents is not floored down an extra cent. Unlock
 * amounts must never carry a fractional cent — the backend resolves start balances to whole
 * cents and rejects submissions whose unlocked portion lands between cents.
 */
export function floorToCents(value: number): number {
  return Math.floor(Math.round(value * 1e6) / 1e4) / 100;
}

/**
 * The jurisdiction whose locked-in/unlocked rules govern a DC source's money. A governing
 * jurisdiction override replaces every source's own jurisdiction; VB money is never overridden.
 */
export function effectiveJurisdictionId(
  ownJurisdictionId: GoverningJurisdictionTypeId | null,
  overrideJurisdictionId: GoverningJurisdictionTypeId | null
): GoverningJurisdictionTypeId | null {
  return overrideJurisdictionId ?? ownJurisdictionId;
}

/** Amount of the locked-in balance being shifted to the non-locked-in side via the unlock flow. */
export function unlockedPortion(
  lockedBalance: number,
  amountToUnlock: number | null,
  percentageToUnlock: number | null
): number {
  if (amountToUnlock !== null) return amountToUnlock;
  if (percentageToUnlock !== null) return floorToCents(lockedBalance * (percentageToUnlock / 100));
  return 0;
}

export function effectiveLockedBalance(
  lockedBalance: number,
  amountToUnlock: number | null,
  percentageToUnlock: number | null
): number {
  return NumberUtils.subtractFloats(lockedBalance, [
    unlockedPortion(lockedBalance, amountToUnlock, percentageToUnlock)
  ]);
}

export function effectiveUnlockedBalance(
  lockedBalance: number,
  unlockedBalance: number,
  amountToUnlock: number | null,
  percentageToUnlock: number | null
): number {
  return NumberUtils.addFloats([unlockedBalance, unlockedPortion(lockedBalance, amountToUnlock, percentageToUnlock)]);
}

export type DcJurisdictionDetailFormGroup =
  | VariableBenefitConfigurationJurisdictionDetailFormGroup
  | CreateMonthlyPensionAccountDcJurisdictionDetailFormGroup;

/**
 * Pooled unlocks are percentage only. The unlock is stored per source jurisdiction, and a
 * percentage applies cleanly to each pot where a dollar amount wouldn't.
 */
export type UnlockMode = 'pooled' | 'single';

export interface UnlockableRow {
  formGroup: DcJurisdictionDetailFormGroup;
  lockedBalance: number;
  canUnlock: boolean;
}

export interface UnlockGroup<T extends UnlockableRow> {
  rows: T[];
  mode: UnlockMode;
}

export interface UnlockCoverage<T extends UnlockableRow> {
  header: UnlockGroup<T> | null;
  perRow: UnlockGroup<T>[];
}

export interface UnlockRestrictions {
  isPhasedRetirement: boolean;
  isBoardDirected: boolean;
}

/**
 * Which Unlock links a DC asset step shows, and which rows each one covers:
 * - A jurisdiction in use (`finalLocationId` set), or one original jurisdiction: one header Unlock
 *   covering every row with locked-in funds, pooled when those come from 2+ jurisdictions.
 * - Default(s) (`finalLocationId` null) with 2+ original jurisdictions: an Unlock on each row.
 * - Phased retirement or a board-directed VB: no Unlock anywhere.
 * Rows without locked-in funds have nothing to unlock and are never covered. `canUnlock` is the
 * caller's decision; it drops a row from coverage without moving the other Unlocks.
 */
export function unlockCoverage<T extends UnlockableRow>(
  rows: T[],
  finalLocationId: GoverningJurisdictionTypeId | null,
  restrictions: UnlockRestrictions
): UnlockCoverage<T> {
  const none: UnlockCoverage<T> = { header: null, perRow: [] };
  if (restrictions.isPhasedRetirement || restrictions.isBoardDirected) return none;

  const lockableRows = rows.filter(
    row => row.formGroup.controls.governingJurisdictionId.value !== null && row.lockedBalance > 0
  );
  const originalJurisdictionCount = new Set(
    lockableRows.map(row => row.formGroup.controls.governingJurisdictionId.value)
  ).size;
  const unlockableRows = lockableRows.filter(row => row.canUnlock);

  if (finalLocationId === null && originalJurisdictionCount > 1) {
    return { header: null, perRow: unlockableRows.map(row => ({ rows: [row], mode: 'single' })) };
  }
  if (unlockableRows.length === 0) return none;

  const mode: UnlockMode = unlockableRows.length > 1 ? 'pooled' : 'single';
  return { header: { rows: unlockableRows, mode }, perRow: [] };
}

interface JurisdictionBalanceRow {
  formGroup:
    | VariableBenefitConfigurationJurisdictionDetailFormGroup
    | CreateMonthlyPensionAccountDcJurisdictionDetailFormGroup;
  lockedBalance: number;
  unlockedBalance: number;
}

/**
 * Whether any jurisdiction row still has funds on the given side of the ledger, using *effective*
 * balances — the original balances adjusted for anything moved across via the unlock flow.
 * Re-evaluates whenever a row's unlock amount/percentage changes, so unlocking 100% of a
 * jurisdiction's locked funds enables "All Non-Locked-In" and disables "All Locked-In".
 */
export function hasAnyEffectiveBalance$(
  rows$: Observable<JurisdictionBalanceRow[]>,
  section: BalanceSection
): Observable<boolean> {
  return rows$.pipe(
    switchMap(rows => {
      const unlockChanges$ = rows.map(row =>
        merge(
          row.formGroup.controls.lockedAmountToUnlock.valueChanges,
          row.formGroup.controls.lockedPercentageToUnlock.valueChanges
        )
      );
      return merge(...unlockChanges$).pipe(
        startWith(null),
        map(() => rows.some(row => effectiveBalanceForRow(row, section) > 0))
      );
    })
  );
}

export function hasAnyUnlock$(formGroups: DcJurisdictionDetailFormGroup[]): Observable<boolean> {
  const unlockChanges$ = formGroups.map(({ controls }) =>
    merge(controls.lockedAmountToUnlock.valueChanges, controls.lockedPercentageToUnlock.valueChanges)
  );
  return merge(...unlockChanges$).pipe(
    startWith(null),
    map(() =>
      formGroups.some(
        ({ controls }) =>
          controls.lockedAmountToUnlock.value !== null || controls.lockedPercentageToUnlock.value !== null
      )
    )
  );
}

function effectiveBalanceForRow(row: JurisdictionBalanceRow, section: BalanceSection): number {
  const amount = row.formGroup.controls.lockedAmountToUnlock.value;
  const percentage = row.formGroup.controls.lockedPercentageToUnlock.value;
  return section === 'locked'
    ? effectiveLockedBalance(row.lockedBalance, amount, percentage)
    : effectiveUnlockedBalance(row.lockedBalance, row.unlockedBalance, amount, percentage);
}

/**
 * An "All X" selection is only valid while side X actually has funds. When the unlock flow
 * empties the selected side (e.g. "All Locked-In" is selected, then 100% is unlocked), fall back
 * to "Specify Amount" — leaving a now-disabled radio selected would force a $0 selection the
 * backend rejects. Emits normally so type-change listeners re-apply their rules.
 */
export function resetAssetTypeIfSideEmpty(
  typeControl: FormControl<AssetSelectionTypeEnum>,
  hasAnyLocked: boolean,
  hasAnyUnlocked: boolean
): void {
  const type = typeControl.value;
  const sideEmpty =
    (type === AssetSelectionTypeEnum.AllLockedIn && !hasAnyLocked) ||
    (type === AssetSelectionTypeEnum.AllNonLockedIn && !hasAnyUnlocked);
  if (sideEmpty) {
    typeControl.setValue(AssetSelectionTypeEnum.SpecifyAmount);
  }
}

/** Whether to render the confirmation check icon next to a section, given the current type. */
export function shouldShowCheck(
  type: AssetSelectionTypeEnum | null,
  desiredAmount: number | null,
  section: BalanceSection
): boolean {
  switch (type) {
    case AssetSelectionTypeEnum.AllLockedIn:
      return section === 'locked';
    case AssetSelectionTypeEnum.AllNonLockedIn:
      return section === 'unlocked';
    case AssetSelectionTypeEnum.SpecifyAmount:
      return (desiredAmount ?? 0) > 0;
    default:
      return false;
  }
}

// Per-type "use entire X balance" checkbox behaviour. `true`/`false` forces the value and
// disables the checkbox; `'toggleable'` leaves the value alone and enables user interaction.
type CheckboxBehaviour = boolean | 'toggleable';

interface AssetTypeCheckboxBehaviour {
  useEntireLockedBalance: CheckboxBehaviour;
  useEntireUnlockedBalance: CheckboxBehaviour;
}

const ASSET_TYPE_CHECKBOX_BEHAVIOUR: Record<AssetSelectionTypeEnum, AssetTypeCheckboxBehaviour> = {
  [AssetSelectionTypeEnum.AllLockedIn]: {
    useEntireLockedBalance: true,
    useEntireUnlockedBalance: false
  },
  [AssetSelectionTypeEnum.AllNonLockedIn]: {
    useEntireLockedBalance: false,
    useEntireUnlockedBalance: true
  },
  [AssetSelectionTypeEnum.SpecifyAmount]: {
    // Both sides have a "Use All" checkbox the user controls: checking it auto-fills the desired
    // amount from the effective balance and disables the input; unchecking re-enables it.
    useEntireLockedBalance: 'toggleable',
    useEntireUnlockedBalance: 'toggleable'
  }
};

/**
 * Apply the per-row checkbox rules for the given asset selection type.
 *
 * `hasLockedBalance` / `hasUnlockedBalance` must reflect the *effective* balances — the original
 * balance adjusted for anything moved across via the unlock flow — not the original balances. A
 * "Use All" checkbox is only meaningful when its side actually has funds: when the effective
 * balance is 0 the checkbox is forced off and disabled (the backend rejects useEntire* === true,
 * and a $0 selection, for a jurisdiction with nothing to use).
 */
export function applyAssetTypeRulesToRow(
  row:
    | VariableBenefitConfigurationJurisdictionDetailFormGroup
    | CreateMonthlyPensionAccountDcJurisdictionDetailFormGroup,
  type: AssetSelectionTypeEnum,
  hasLockedBalance: boolean,
  hasUnlockedBalance: boolean
): void {
  const rules = ASSET_TYPE_CHECKBOX_BEHAVIOUR[type];
  applyCheckboxBehaviour(row.controls.useEntireLockedBalance, hasLockedBalance ? rules.useEntireLockedBalance : false);
  applyCheckboxBehaviour(
    row.controls.useEntireUnlockedBalance,
    hasUnlockedBalance ? rules.useEntireUnlockedBalance : false
  );
}

function applyCheckboxBehaviour(control: FormControl<boolean>, behaviour: CheckboxBehaviour): void {
  if (behaviour === 'toggleable') {
    control.enable({ emitEvent: false });
    return;
  }
  // setValue emits so each row's auto-fill reacts (mirrors the toggle to the desired balance).
  if (control.value !== behaviour) {
    control.setValue(behaviour);
  }
  control.disable({ emitEvent: false });
}

const TYPES_ALLOWING_CUSTOM_LOCKED_AMOUNT: ReadonlySet<AssetSelectionTypeEnum> = new Set([
  AssetSelectionTypeEnum.AllNonLockedIn,
  AssetSelectionTypeEnum.SpecifyAmount
]);

/**
 * On a real switch into a type where the locked amount is user-driven, reset the locked side to a
 * clean slate: uncheck "Use All", clear the amount, and re-enable the input for manual entry. This
 * keeps the checkbox and input consistent (a checked box always implies an auto-filled, disabled
 * input) regardless of what the previous type had set.
 */
export function clearLockedAmountsForTypeSwitch(
  rows: FormArray<
    VariableBenefitConfigurationJurisdictionDetailFormGroup | CreateMonthlyPensionAccountDcJurisdictionDetailFormGroup
  >,
  type: AssetSelectionTypeEnum
): void {
  if (!TYPES_ALLOWING_CUSTOM_LOCKED_AMOUNT.has(type)) return;
  rows.controls.forEach(row => {
    row.controls.useEntireLockedBalance.setValue(false, { emitEvent: false });
    row.controls.desiredLockedStartBalance.setValue(null, { emitEvent: false });
    row.controls.desiredLockedStartBalance.enable({ emitEvent: false });
  });
}

/**
 * Switching back to "Specify Amount" starts the non-locked-in side unselected too, so neither
 * "All X" choice carries over. Emits so each row's auto-fill clears the desired unlocked amount.
 */
export function clearUnlockedSelectionForSpecifyAmount(
  rows: FormArray<DcJurisdictionDetailFormGroup>,
  type: AssetSelectionTypeEnum
): void {
  if (type !== AssetSelectionTypeEnum.SpecifyAmount) return;
  rows.controls.forEach(row => row.controls.useEntireUnlockedBalance.setValue(false));
}

/**
 * Wires auto-fill behaviour for a single jurisdiction row:
 * - When `useEntireLockedBalance` flips on (or funds are unlocked while it's on), fill and lock
 *   `desiredLockedStartBalance` with the effective (post-unlock) locked balance. When it flips
 *   off, clear and re-enable the input for manual entry.
 * - When `useEntireUnlockedBalance` flips on (or the unlock amount/percentage changes while
 *   on), mirror the effective unlocked balance into `desiredUnlockedStartBalance`. The
 *   desired unlocked field is always disabled — it's auto-maintained, never user-editable.
 */
export function wireJurisdictionRowAutoFill(
  row:
    | VariableBenefitConfigurationJurisdictionDetailFormGroup
    | CreateMonthlyPensionAccountDcJurisdictionDetailFormGroup,
  lockedBalance: number,
  unlockedBalance: number,
  destroyRef: DestroyRef
): void {
  const {
    useEntireLockedBalance,
    desiredLockedStartBalance,
    useEntireUnlockedBalance,
    desiredUnlockedStartBalance,
    lockedAmountToUnlock,
    lockedPercentageToUnlock
  } = row.controls;

  // Locked side — when "Use All" is on, fill and lock the input with the effective (post-unlock)
  // locked balance; when off, leave the user's manual amount alone and keep the input editable.
  function fillLockedFromEffective(): void {
    const effective = effectiveLockedBalance(lockedBalance, lockedAmountToUnlock.value, lockedPercentageToUnlock.value);
    desiredLockedStartBalance.setValue(effective, { emitEvent: false });
    desiredLockedStartBalance.disable({ emitEvent: false });
  }

  if (useEntireLockedBalance.value) {
    fillLockedFromEffective();
  } else {
    desiredLockedStartBalance.enable({ emitEvent: false });
  }

  useEntireLockedBalance.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(useEntire => {
    if (useEntire) {
      fillLockedFromEffective();
    } else {
      desiredLockedStartBalance.setValue(null, { emitEvent: false });
      desiredLockedStartBalance.enable({ emitEvent: false });
    }
  });

  // While "Use All" is on, unlocking funds lowers the effective locked balance, so keep the
  // read-only amount in sync. When off, the unlock flow must not touch the manual amount.
  merge(lockedAmountToUnlock.valueChanges, lockedPercentageToUnlock.valueChanges)
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(() => {
      if (useEntireLockedBalance.value) fillLockedFromEffective();
    });

  // Unlocked side — the input is always disabled; we just keep its value in sync.
  // The desired unlocked balance strictly follows the "Use All" checkbox: when on, mirror the
  // effective unlocked balance (original non-locked-in funds plus anything unlocked from the
  // locked side); when off, null. Using any non-locked-in funds requires "Use All" — the backend
  // rejects both a partial amount and $0, so an unselected checkbox must send null.
  function desiredUnlockedFor(useEntire: boolean, amount: number | null, percentage: number | null): number | null {
    if (!useEntire) return null;
    return effectiveUnlockedBalance(lockedBalance, unlockedBalance, amount, percentage);
  }

  desiredUnlockedStartBalance.disable({ emitEvent: false });
  desiredUnlockedStartBalance.setValue(
    desiredUnlockedFor(useEntireUnlockedBalance.value, lockedAmountToUnlock.value, lockedPercentageToUnlock.value),
    { emitEvent: false }
  );
  // skip(1) — the startWith emission just repeats the initial state we've already applied above.
  combineLatest([
    useEntireUnlockedBalance.valueChanges.pipe(startWith(useEntireUnlockedBalance.value)),
    lockedAmountToUnlock.valueChanges.pipe(startWith(lockedAmountToUnlock.value)),
    lockedPercentageToUnlock.valueChanges.pipe(startWith(lockedPercentageToUnlock.value))
  ])
    .pipe(skip(1), takeUntilDestroyed(destroyRef))
    .subscribe(([useEntire, amount, percentage]) => {
      desiredUnlockedStartBalance.setValue(desiredUnlockedFor(useEntire, amount, percentage), { emitEvent: false });
      desiredUnlockedStartBalance.disable({ emitEvent: false });
    });
}
